import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from './hooks/useAuth';
import { setPersonaTheme } from './lib/theme';
import StoryLanding from './pages/StoryLanding';
import LandingChat from './pages/LandingChat';
import OnboardingPage from './pages/OnboardingPage';
import UserDashboard from './pages/UserDashboard';
import ClinicDashboard from './pages/ClinicDashboard';
import AdminDashboard from './pages/AdminDashboard';
import ClinicSignup from './pages/ClinicSignup';
import ClinicLanding from './pages/ClinicLanding';
import StripeSuccess from './pages/StripeSuccess';

function LoadingScreen() {
  const { t } = useTranslation();
  return (
    <div style={{
      minHeight: '100vh', background: '#F8F4EF', display: 'flex',
      flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      fontFamily: 'sans-serif',
    }}>
      <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🌿</div>
      <div style={{ color: '#2D6A4F', fontFamily: 'Georgia, serif', fontSize: '1.2rem' }}>{t('app.loading')}</div>
    </div>
  );
}

function getUrlParam(key) {
  return new URLSearchParams(window.location.search).get(key);
}

export default function App() {
  const { user, profile, loading, signUpAnonymous, signOut, fetchProfile } = useAuth();
  const [storySeen, setStorySeen] = useState(() => !!localStorage.getItem('vera_story_seen'));

  // Debe aplicarse en el cuerpo del componente (no en un useEffect) para que
  // el primer render de los hijos ya use los colores correctos, sin parpadeo.
  setPersonaTheme(profile?.persona);

  useEffect(() => {
    const refCode = getUrlParam('ref');
    if (refCode) sessionStorage.setItem('vera_ref_code', refCode);
  }, []);

  const page = getUrlParam('page');

  if (page === 'clinic-landing') {
    return <ClinicLanding />;
  }
  if (page === 'clinic-signup') {
    return <ClinicSignup onBack={() => window.location.href = '/?page=clinic-landing'} />;
  }
  if (page === 'success') {
    return <StripeSuccess />;
  }

  if (loading) return <LoadingScreen />;

  if (!user) {
    if (!storySeen) {
      return (
        <StoryLanding
          onEnter={() => {
            localStorage.setItem('vera_story_seen', '1');
            setStorySeen(true);
          }}
        />
      );
    }
    return <LandingChat onSignUpAnonymous={signUpAnonymous} onCodeRedeemed={() => fetchProfile(user.id)} />;
  }

  const role = profile?.role;

  if (role === 'admin') {
    return <AdminDashboard user={user} profile={profile} onSignOut={signOut} />;
  }

  if (role === 'clinic') {
    return <ClinicDashboard user={user} profile={profile} onSignOut={signOut} />;
  }

  if (profile && !profile.clinic_id && role === 'patient') {
    return (
      <LandingChat
        onSignUpAnonymous={signUpAnonymous}
        onCodeRedeemed={() => fetchProfile(user.id)}
        showCodeEntry
      />
    );
  }

  if (!profile || !profile.onboarding_done) {
    return (
      <OnboardingPage
        userId={user.id}
        clinicId={profile?.clinic_id}
        onComplete={() => fetchProfile(user.id)}
      />
    );
  }

  return <UserDashboard user={user} profile={profile} onSignOut={signOut} onProfileUpdate={() => fetchProfile(user.id)} />;
}
